import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
  Alert,
  Box,
  CircularProgress,
  useTheme,
} from "@mui/material";

import ReceiptIcon from "@mui/icons-material/Receipt";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

import {
  getDeliveryCenterConfig,
  setDeliveryCenterConfig,
} from "../../../api/DeliveryCenter/deliveryCenter";
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import ConfigSwitchCard from "../../styled/ConfigSwitchCard";

// Define configuration options
const configOptions = [
  {
    id: "show_priority_on_special_orders",
    title: "Mostrar PRIORIDADE em pedidos especiais",
    description: {
      enabled:
        "Prioridade Ativada: Exibe uma marcação de prioridade em comandas de pedidos especiais.",
      disabled:
        "Prioridade Desativada: Não exibe marcação de prioridade em comandas de pedidos especiais.",
    },
    icon: PriorityHighIcon,
    successMessage: (value) =>
      `Prioridade para pedidos especiais ${
        value ? "ativada" : "desativada"
      } com sucesso`,
    errorMessage:
      "Erro ao atualizar a configuração de prioridade para pedidos especiais",
    notificationId: "priority-special-orders-config",
  },
  {
    id: "show_priority_for_saloon_orders",
    title: "Mostrar PRIORIDADE para pedidos de salão",
    description: {
      enabled:
        "Prioridade Ativada: Exibe uma marcação de prioridade em comandas de pedidos do salão.",
      disabled:
        "Prioridade Desativada: Não exibe marcação de prioridade em comandas de pedidos do salão.",
    },
    icon: PriorityHighIcon,
    successMessage: (value) =>
      `Prioridade para pedidos de salão ${
        value ? "ativada" : "desativada"
      } com sucesso`,
    errorMessage:
      "Erro ao atualizar a configuração de prioridade para pedidos de salão",
    notificationId: "priority-saloon-orders-config",
  },
];

export default function OrderTicketConfigsModal({ show, closeModal }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [configValues, setConfigValues] = useState({});

  useEffect(() => {
    if (!show) return;
    loadConfigs();
  }, [show]);

  const loadConfigs = async () => {
    try {
      setLoading(true);

      const configParams = configOptions.map((option) => option.id).join(",");
      const params = { config: configParams };
      const { data } = await getDeliveryCenterConfig(params);

      setConfigValues(data);
    } catch (err) {
      console.log(err);
      setErrorMessage(
        "Ocorreu um erro ao carregar as configurações. Tente novamente."
      );
      setShowError(true);
      dispatchSnackBarMessage(
        "configs-load-error",
        "Erro ao carregar configurações",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = async (configId, event) => {
    try {
      const newValue = event.target.checked;
      const configOption = configOptions.find(
        (option) => option.id === configId
      );

      if (!configOption) return;

      const data = {
        config: {
          [configId]: newValue,
        },
      };

      await setDeliveryCenterConfig(data);

      setConfigValues((prev) => ({
        ...prev,
        [configId]: newValue,
      }));

      dispatchSnackBarMessage(
        configOption.notificationId,
        configOption.successMessage(newValue),
        "success"
      );
    } catch (e) {
      console.error(e);
      const configOption = configOptions.find(
        (option) => option.id === configId
      );

      dispatchSnackBarMessage(
        `${configId}-update-error`,
        configOption?.errorMessage || "Erro ao atualizar configuração",
        "error"
      );
    }
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="600px"
      maxWidth="90%"
    >
      <ModalHeader
        title="Configurações da Comanda"
        icon={ReceiptIcon}
        onClose={closeModal}
      />

      <ModalContent>
        <Box sx={{ px: 3, pt: 2, pb: 4 }}>
          <AnimatePresence>
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {errorMessage}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            configOptions.map((config) => {
              const isChecked = configValues[config.id] || false;

              return (
                <ConfigSwitchCard
                  key={config.id}
                  icon={config.icon}
                  title={config.title}
                  description={
                    isChecked
                      ? config.description.enabled
                      : config.description.disabled
                  }
                  isActive={isChecked}
                  onChange={(e) => handleConfigChange(config.id, e)}
                />
              );
            })
          )}
        </Box>
      </ModalContent>
    </AnimatedModal>
  );
}
